import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectGroupGuid } from 'slices/commonSelectors';

import type {
  HwcgContactListId,
  HwcgGroupId,
} from 'components/IncidentConfig/CreateIncident/hwcgGroupActions';

export type HwcgPocContact = 'primary' | 'secondary';

export type HwcgContactListMember = {
  id: string;
  userGuid: string | null;
  name: string;
  memberCompany: string | null;
  serviceProvider: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  primaryCategory: string | null;
  secondaryCategory: string | null;
  pocContact: HwcgPocContact | null;
  authorizedIndividual: boolean;
};

export type HwcgContactWrite = {
  constituency: HwcgContactListId;
  userGuid?: string | null;
  memberCompany?: string | null;
  serviceProvider?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  primaryCategory?: string | null;
  secondaryCategory?: string | null;
  pocContact?: HwcgPocContact | null;
  authorizedIndividual?: boolean;
};

export type HwcgContactList = {
  id: string;
  constituency: HwcgGroupId;
  label: string;
  members: HwcgContactListMember[];
};

export const hwcgContactListKeys = {
  all: ['hwcg-contact-lists'] as const,
  list: (groupGuid: string) =>
    [...hwcgContactListKeys.all, 'list', groupGuid] as const,
};

function contactListError(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: unknown } } })
    ?.response?.data?.error;
  if (typeof data === 'string' && data.trim()) return data;
  return fallback;
}

export function useHwcgContactLists(options?: {
  enabled?: boolean;
  groupGuid?: string;
}) {
  const selectedGroupGuid = useSelector(selectGroupGuid);
  const groupGuid = options?.groupGuid || selectedGroupGuid || '';
  return useQuery({
    queryKey: hwcgContactListKeys.list(groupGuid),
    enabled: Boolean(groupGuid) && options?.enabled !== false,
    queryFn: async () => {
      const response = await axios.get<HwcgContactList[]>(
        config.apiGateway.hwcgContactLists,
        { params: { groupGuid } }
      );
      return response.data ?? [];
    },
  });
}

function useSaveContact(method: 'post' | 'put', contactId?: string) {
  const groupGuid = useSelector(selectGroupGuid) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: HwcgContactWrite) => {
      const url =
        method === 'post'
          ? `${config.apiGateway.hwcgContactLists}/contacts`
          : `${config.apiGateway.hwcgContactLists}/contacts/${contactId}`;
      const response = await axios.request<HwcgContactList[]>({
        method,
        url,
        data: { ...payload, groupGuid },
      });
      return response.data;
    },
    onSuccess: (lists) => {
      queryClient.setQueryData(hwcgContactListKeys.list(groupGuid), lists);
    },
    onError: (error) =>
      toast.error(contactListError(error, 'Could not save that contact.')),
  });
}

export function useAddHwcgContact() {
  return useSaveContact('post');
}

export function useUpdateHwcgContact(contactId: string) {
  return useSaveContact('put', contactId);
}

export function useRemoveHwcgContact() {
  const groupGuid = useSelector(selectGroupGuid) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: string) => {
      const response = await axios.delete<HwcgContactList[]>(
        `${config.apiGateway.hwcgContactLists}/contacts/${contactId}`,
        { params: { groupGuid } }
      );
      return response.data;
    },
    onSuccess: (lists) => {
      queryClient.setQueryData(hwcgContactListKeys.list(groupGuid), lists);
    },
    onError: (error) =>
      toast.error(contactListError(error, 'Could not remove that contact.')),
  });
}
